export type OverTimeRow = {
  period: string;
  lab_id: string | null;
  lab_name: string | null;
  city: string | null;
  completed: number;
  planned: number;
};

/**
 * One row per period with a column per series. Every row carries every series,
 * because AutoChart reads its series from the first row's keys.
 */
export function pivotOverTime(
  rows: OverTimeRow[],
  label: (r: OverTimeRow) => string,
  metric: string,
): Record<string, unknown>[] {
  const count = (r: OverTimeRow) =>
    metric === "completed"
      ? r.completed
      : metric === "planned"
        ? r.planned
        : r.completed + r.planned;
  const series = [...new Set(rows.map(label))];
  const byPeriod = new Map<string, Record<string, unknown>>();
  for (const r of rows) {
    let entry = byPeriod.get(r.period);
    if (!entry) {
      entry = { period: r.period, ...Object.fromEntries(series.map((n) => [n, 0])) };
      byPeriod.set(r.period, entry);
    }
    const key = label(r);
    entry[key] = (entry[key] as number) + count(r);
  }
  return [...byPeriod.values()];
}

/** One row per (period, label) with both metrics summed — the CSV counterpart of the pivot. */
export function groupOverTime(
  rows: OverTimeRow[],
  label: (r: OverTimeRow) => string,
): { period: string; label: string; completed: number; planned: number }[] {
  const groups = new Map<string, { period: string; label: string; completed: number; planned: number }>();
  for (const r of rows) {
    const name = label(r);
    const key = JSON.stringify([r.period, name]);
    const g = groups.get(key) ?? { period: r.period, label: name, completed: 0, planned: 0 };
    g.completed += r.completed;
    g.planned += r.planned;
    groups.set(key, g);
  }
  return [...groups.values()];
}
